import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { CardContent } from '@/shared/components/card-content/card-content';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';

import type { AverbacaoNomeResponse } from '@/core/api/sicom-api/servicos/pessoa-fisica/averbacao-nome/averbacao-nome.model';
import { AverbacaoNomeServicosService } from '@/features/servicos/pessoa-fisica/content/averbacao-nome-servicos/averbacao-nome-servicos.service';
import { formatarDataHoraMascara, formatarDataMascara, montarRequestProtocolo, valorTexto, type ProtocoloResumoConsulta } from '@/features/servicos/shared/utils/protocolo-consulta.utils';
import { iconeDaSecao, secaoEhDeDados, subtituloDaSecao } from '@/features/servicos/shared/utils/secao-detalhamento.utils';
import { rotuloBadgeSituacao, situacaoClassePorTexto } from '@/features/servicos/shared/utils/protocolo-rotulos.utils';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface CampoVisualizacao {
    label: string;
    chave: keyof AverbacaoNomeResponse;
    formatador?: (valor: unknown) => string;
}

interface SecaoVisualizacao {
    titulo: string;
    campos: CampoVisualizacao[];
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-averbacao-nome-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, InputTextModule, CardContent, DialogModule, VisualizadorPdf],
    templateUrl: './averbacao-nome-servicos.html',
    styleUrl: './averbacao-nome-servicos.scss'
})
export class AverbacaoNomeServicos {
    /**
     * Id do controle de um campo, para o `<label for>` apontar para ele. O id junta seção e rótulo
     * porque os rótulos se repetem entre seções — só o rótulo geraria ids duplicados, e o clique no
     * label levaria o foco para o campo da outra seção.
     */
    idDoCampo(tituloDaSecao: string, rotulo: string): string {
        return `${this.emSlug(tituloDaSecao)}-${this.emSlug(rotulo)}`;
    }

    /** Texto reduzido a `a-z0-9-`, para servir de id de elemento. */
    private emSlug(texto: string): string {
        return (texto ?? '')
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    }

    /**
     * Apresentação das seções — ícone, subtítulo e se a seção é de dados — vem do vocabulário
     * compartilhado: o mesmo título tem a mesma cara em todos os detalhamentos.
     */
    protected readonly iconeDaSecao = iconeDaSecao;
    protected readonly subtituloDaSecao = subtituloDaSecao;
    protected readonly secaoEhDeDados = secaoEhDeDados;
    protected readonly situacaoClassePorTexto = situacaoClassePorTexto;
    protected readonly rotuloBadgeSituacao = rotuloBadgeSituacao;

    readonly protocoloSelecionado = input<ProtocoloResumoConsulta | null>(null);
    readonly codigoEstado = input('');
    readonly dataHoraSolicitacaoAtualizada = output<string>();

    readonly carregando = signal(false);
    readonly erroConsulta = signal<string | null>(null);
    readonly dados = signal<AverbacaoNomeResponse | null>(null);
    readonly exibirDialogAnexo = signal(false);
    readonly blobAnexo = signal<Blob | null>(null);
    readonly nomeArquivoAnexo = signal('anexo-averbacao-nome.pdf');

    readonly secoes: SecaoVisualizacao[] = [
        {
            titulo: 'Protocolo',
            campos: [
                { label: 'Número Protocolo', chave: 'numeroProtocolo' },
                { label: 'Ano Protocolo', chave: 'anoProtocolo' },
                { label: 'Data/Hora Solicitacao', chave: 'dataHora', formatador: formatarDataHoraMascara },
                { label: 'Situação', chave: 'situacao', formatador: this.formatarSituacao }
            ]
        },
        {
            titulo: 'Dados do Solicitante',
            campos: [
                { label: 'Nome Atual', chave: 'nome' },
                { label: 'Nome Alterado', chave: 'nomeAlterado' },
                { label: 'CPF', chave: 'cpf' },
                { label: 'CRM', chave: 'numCrm' },
                { label: 'UF CRM Origem', chave: 'ufCrmOrigem' },
                { label: 'Médico Ativo', chave: 'medicoAtivo' },
                { label: 'Email Solicitante', chave: 'emailSolicitante', formatador: this.formatarEmails }
            ]
        },
        {
            titulo: 'Documento Comprobatório',
            campos: [
                { label: 'Tipo de Documento', chave: 'tipoDocumento' },
                { label: 'Número da Certidão', chave: 'numeroCertidao' }
            ]
        },
        {
            titulo: 'Pagamento',
            campos: [
                { label: 'Valor', chave: 'valor', formatador: this.formatarMoeda },
                { label: 'Data Emissão', chave: 'dataEmissao', formatador: formatarDataMascara },
                { label: 'Data Vencimento', chave: 'dataVencimento', formatador: formatarDataMascara },
                { label: 'Nosso Número', chave: 'nossoNumero' },
                { label: 'Banco', chave: 'banco' },
                { label: 'Número Convênio', chave: 'numeroConvenio' },
                { label: 'Número Documento', chave: 'numeroDocumento' },
                { label: 'Identificador Pagamento', chave: 'identificadorPagamento' }
            ]
        }
    ];

    private readonly destroyRef = inject(DestroyRef);
    private ultimaChaveConsulta = '';

    private readonly servico = inject(AverbacaoNomeServicosService);
    private readonly messageService = inject(MessageService);

    constructor() {
        // Recarrega ao mudar protocolo/UF; o guard ultimaChaveConsulta evita repetir a mesma consulta.
        effect(() => {
            this.protocoloSelecionado();
            this.codigoEstado();
            this.carregarDados();
        });
    }

    valorCampo(campo: CampoVisualizacao): string {
        const dados = this.dados();
        if (!dados) {
            return '-';
        }

        const valorBruto = dados[campo.chave];
        if (valorBruto === null || valorBruto === undefined || valorTexto(valorBruto).trim() === '') {
            return '-';
        }

        if (campo.formatador) {
            const valorFormatado = campo.formatador(valorBruto);
            return valorFormatado ? valorFormatado : '-';
        }

        return valorTexto(valorBruto);
    }

    possuiAnexo(): boolean {
        return !!valorTexto(this.dados()?.idAnexo).trim();
    }

    onVisualizarAnexo(event: Event): void {
        event.preventDefault();
        const idAnexo = valorTexto(this.dados()?.idAnexo).trim();
        if (!idAnexo) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Id do anexo não identificado.' });
            return;
        }

        this.servico
            .visualizarAnexo(idAnexo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                    this.exibirDialogAnexo.set(true);
                },
                error: () => {
                    // Mensagem fica a cargo do interceptor central; aqui so o estado local.
                    this.blobAnexo.set(null);
                    this.exibirDialogAnexo.set(false);
                }
            });
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    deveDestacarIdentificacao(secaoTitulo: string, chave: keyof AverbacaoNomeResponse): boolean {
        if (secaoTitulo !== 'Dados do Solicitante') {
            return false;
        }

        return chave === 'nomeAlterado' || chave === 'cpf' || chave === 'numCrm' || chave === 'ufCrmOrigem';
    }

    private carregarDados(): void {
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());

        if (!request) {
            this.ultimaChaveConsulta = '';
            this.dados.set(null);
            this.carregando.set(false);
            this.erroConsulta.set(null);
            this.dataHoraSolicitacaoAtualizada.emit('');
            return;
        }

        const chaveConsulta = `${request.numeroProtocolo}/${request.anoProtocolo}-${request.ufCrm}`;
        if (chaveConsulta === this.ultimaChaveConsulta) {
            return;
        }

        this.ultimaChaveConsulta = chaveConsulta;
        this.carregando.set(true);
        this.erroConsulta.set(null);

        this.servico
            .consultar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.dados.set(response ?? null);
                    this.carregando.set(false);
                    this.dataHoraSolicitacaoAtualizada.emit(formatarDataHoraMascara(response?.dataHora));
                },
                error: () => {
                    this.dados.set(null);
                    this.carregando.set(false);
                    this.erroConsulta.set('Falha ao carregar os dados de averbação de nome.');
                    this.dataHoraSolicitacaoAtualizada.emit('');
                }
            });
    }

    private formatarMoeda(valor: unknown): string {
        const numero = Number(valor);
        if (Number.isFinite(numero)) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numero);
        }

        const texto = valorTexto(valor).trim();
        if (!texto) {
            return '';
        }

        const normalizado = Number(texto.replace(/\./g, '').replace(',', '.'));
        if (Number.isFinite(normalizado)) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(normalizado);
        }

        return texto;
    }

    private formatarSituacao(valor: unknown): string {
        const codigo = valorTexto(valor).trim().toUpperCase();
        switch (codigo) {
            case 'A':
                return 'Ativo';
            case 'T':
                return 'Tramitado';
            case 'P':
                return 'Pendência';
            case 'C':
                return 'Cancelado';
            case 'E':
                return 'Encerrado';
            default:
                return valorTexto(valor);
        }
    }

    private formatarEmails(valor: unknown): string {
        const texto = valorTexto(valor).trim();
        if (!texto) {
            return '';
        }

        return texto
            .split(';')
            .map((email) => email.trim())
            .filter((email) => email.length > 0)
            .join('\n');
    }
}
